import { useEffect, useRef, useState } from "react";
import { AllReceivingInvoicesViewModel } from "../../../viewmodels/receiving/invoices/AllReceivingInvoicesViewModel";
import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";

import { connect } from "react-redux";

import * as actions from "../../../store/actions/index";

import { formatDate, withRouter } from "../../../shared/utility";

import EmptyModal from "../../../components/modals/EmptyModal";
import SlideOver from "../../../components/slideOver/SlideOver";

import { useNavigate } from "react-router-dom";

import { Paginator } from "primereact/paginator";
import DateRangeFilter from "../../../components/filterDropdown/DateRangeFilter";
import ActionButton from "../../../components/controls/ActionButton";
import ActionLink from "../../../components/controls/ActionLink";

import { formatDateRangeSummary } from "../../../shared/filterUtils";
import AuditActionLink from "../../../components/audit/AuditActionLink";
import AuditHistoriesLink from "../../../components/audit/AuditHistoriesLink";
import OperationalDataTableSkeleton from "../../../components/operational/OperationalDataTableSkeleton";

function AllReceivingInvoices(props) {
  const { addSnackbar, logout } = props;
  const {
    askDeleteReceivingInvoice,
    emptyModal,
    getReceivingInvoices,
    loading,
    receivingInvoices,
    setSlideOver,
    slideOver,
    setEmptyModelOpen,
    filters,
    setFilters,
    showDates,
    setShowDates,
  } = AllReceivingInvoicesViewModel(logout, addSnackbar);

  const navigate = useNavigate();

  let currentPage = 1;
  useEffect(() => {
    getReceivingInvoices(null, currentPage);
  }, []);

  const [first, setFirst] = useState(1);
  const [rows, setRows] = useState(20);

  const searchRef = useRef();

  const handleSearch = () => {
    getReceivingInvoices(searchRef.current.value, currentPage);
  };

  const handlePagination = (event) => {
    currentPage = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    getReceivingInvoices(searchRef.current.value, currentPage);
  };

  function navigateTo(target) {
    navigate(target);
  }

  const hasDateFilter = Boolean(filters.startDate && filters.endDate);
  const rangeSummary = formatDateRangeSummary(
    filters.startDate,
    filters.endDate,
  );

  const invoiceRows = receivingInvoices?.data || [];

  return (
    <div className="min-h-screen bg-[#F6F8FB] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-5">
        <section className="rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <div className="border-b border-[#E2E8F0] px-5 py-5 sm:px-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-3xl">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#64748B]">
                  QA Trace / Receiving
                </p>
                <h1 className="mt-1 text-[1.65rem] font-semibold tracking-tight text-[#0F172A] sm:text-3xl">
                  Receiving Invoices
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-[#64748B]">
                  Manage inbound supplier invoices, receiving records, lot
                  details, and the first step of product traceability.
                </p>
              </div>

              <div className="inline-flex w-fit items-center gap-2 rounded-xl border border-[#E2E8F0] bg-[#F6F8FB] px-3.5 py-2 text-sm font-medium text-[#0F172A]">
                <span className="h-2 w-2 rounded-full bg-[#0D9488]" />
                {receivingInvoices?.total || 0} records
              </div>
            </div>
          </div>

          <div className="grid gap-3 px-5 py-4 sm:px-6 lg:grid-cols-[minmax(280px,430px)_1fr] lg:items-center">
            <div className="flex h-11 overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm focus-within:border-[#2563EB] focus-within:ring-2 focus-within:ring-[#2563EB]/10">
              <span className="inline-flex items-center pl-3 text-[#64748B]/70">
                <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <input
                type="search"
                name="searchReceivingInvoice"
                id="searchReceivingInvoice"
                className="h-full w-full border-0 bg-transparent px-3 text-sm text-[#0F172A] placeholder:text-[#64748B]/70 focus:outline-none focus:ring-0"
                placeholder="Search by supplier name"
                ref={searchRef}
                onKeyDown={(e) => (e.key === "Enter" ? handleSearch() : null)}
              />
              <button
                type="button"
                className="border-l border-[#E2E8F0] px-4 text-sm font-semibold text-[#0F172A] transition hover:bg-[#F6F8FB] hover:text-[#2563EB]"
                onClick={() => handleSearch()}
              >
                Search
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <DateRangeFilter
                onChange={(startDate, endDate) =>
                  setFilters({
                    ...filters,
                    startDate: startDate,
                    endDate: endDate,
                  })
                }
                onClear={() =>
                  setFilters({
                    ...filters,
                    startDate: null,
                    endDate: null,
                  })
                }
                endDate={filters.endDate}
                startDate={filters.startDate}
                setShowDates={setShowDates}
                showDates={showDates}
                applyChanges={handleSearch}
                maxDate={new Date()}
                rangeSummary={rangeSummary}
              />
              <AuditHistoriesLink slug="receiving-invoice" />
              <button
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-[#2563EB] bg-[#2563EB] px-3.5 text-sm font-semibold text-white shadow-sm transition hover:border-[#1D4ED8] hover:bg-[#1D4ED8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563EB]"
                onClick={() => navigateTo("/receiving-invoices/new")}
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                Add receiving
              </button>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          {loading ? (
            <OperationalDataTableSkeleton
              columns={[
                "Invoice #",
                "PO #",
                "Supplier",
                "Received by",
                "Date",
                "Actions",
              ]}
              rows={8}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate border-spacing-0 text-sm">
                <thead>
                  <tr>
                    {[
                      "Invoice #",
                      "PO #",
                      "Supplier",
                      "Received by",
                      "Date",
                    ].map((column, index) => (
                      <th
                        key={column}
                        scope="col"
                        className={`border-b border-[#E2E8F0] bg-[#EFF6FF] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748B] ${
                          index === 0 ? "sm:pl-5" : ""
                        }`}
                      >
                        {column}
                      </th>
                    ))}
                    <th
                      scope="col"
                      className="border-b border-[#E2E8F0] bg-[#EFF6FF] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748B] sm:pr-5"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {invoiceRows.length > 0 ? (
                    invoiceRows.map((receivingInvoice, index) => (
                      <tr
                        key={receivingInvoice.id}
                        className="transition hover:bg-[#F6F8FB]"
                      >
                        <td className="border-b border-[#E2E8F0]/70 px-4 py-3.5 align-middle text-sm font-semibold text-[#0F172A] first:sm:pl-5">
                          {receivingInvoice.invoice_number}
                        </td>
                        <td className="border-b border-[#E2E8F0]/70 px-4 py-3.5 align-middle text-sm font-semibold text-[#0F172A] first:sm:pl-5">
                          {receivingInvoice.purchase_order?.po_number || "—"}
                        </td>
                        <td className="border-b border-[#E2E8F0]/70 px-4 py-3.5 align-middle text-sm text-[#64748B]">
                          {receivingInvoice.supplier?.name || "—"}
                        </td>
                        <td className="border-b border-[#E2E8F0]/70 px-4 py-3.5 align-middle text-sm text-[#64748B]">
                          {receivingInvoice.received_by_user
                            ? receivingInvoice.received_by_user.name
                            : receivingInvoice.recorded_by_user?.username ||
                              "—"}
                        </td>
                        <td className="border-b border-[#E2E8F0]/70 px-4 py-3.5 align-middle text-sm text-[#64748B]">
                          {formatDate(receivingInvoice.date)}
                        </td>
                        <td className="whitespace-nowrap border-b border-[#E2E8F0]/70 px-4 py-3.5 text-right text-sm font-medium sm:pr-5">
                          <div className="inline-flex flex-wrap justify-end gap-2">
                            <ActionLink
                              to={`/receiving-invoices/${receivingInvoice.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Edit
                            </ActionLink>
                            <AuditActionLink
                              slug="receiving-invoice"
                              id={receivingInvoice.id}
                              name={receivingInvoice.invoice_number}
                            />
                            <ActionButton
                              color="red"
                              onClick={() => {
                                askDeleteReceivingInvoice(
                                  receivingInvoice,
                                  index,
                                );
                              }}
                            >
                              Delete
                            </ActionButton>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        className="px-5 py-14 text-center text-sm text-[#64748B]"
                        colSpan={5}
                      >
                        No receiving invoices found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="flex justify-end px-1">
          <Paginator
            first={first}
            rows={rows}
            totalRecords={receivingInvoices?.total || 0}
            onPageChange={handlePagination}
          />
        </div>
      </div>

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AllReceivingInvoices),
);
